import $ from 'jquery';

import TooltipHelper from 'chairisher/view/helper/tooltip';

import { getUrlName } from 'chairisher/context/site';
import { decodeHTMLEntity, formatCurrency } from 'chairisher/util/format';
import {
    getFarthestEditedStep,
    getOfferProductTitle,
    getOrderSummaryCardTitle,
    getPurchaseObject,
    getTaxExemptionButtonText,
    isOffer,
    isPrivateOffer,
} from 'chairisher/context/purchase';
import { showTaxExemptionModal, canShowTaxExemptionModal } from 'chairisher/view/taxexemptionmodal';
import { getPercentOff } from 'chairisher/util/math';

// A modal used for editing an offer's quantity/price. When present, the receipt renders an 'edit' link to open it.
const hasMaoModal = $('#js-mao-modal').length === 1;

const TBD_VALUE_DISPLAY = 'TBD';

/**
 * @enum {string} Types of line items
 */
const LINE_ITEM_TYPES = Object.freeze({
    DUTIES: 'DUT',
    PROCESSING_FEE: 'processing_fee',
    SALES_TAX: 'sales_tax',
    SALES_TAX_BREAKDOWN_ITEM: 'sales_tax_breakdown_item',
    TOTAL: 'total',
});

/**
 * @return {Object|undefined}
 */
export function getProcessingFeeLineItem() {
    const receipt = getPurchaseObject().purchase_details.receipt_summary.value;
    return receipt.find(({ type }) => type === LINE_ITEM_TYPES.PROCESSING_FEE);
}

/**
 * @return {number}
 */
export function getProcessingFeeValue() {
    const processingFee = getProcessingFeeLineItem();
    let processingFeeValue = 0;
    if (processingFee) {
        processingFeeValue = Number(processingFee.value);
    }
    return processingFeeValue;
}

/**
 * @return {Array.<Object>}
 */
function getReceiptSummary() {
    return getPurchaseObject().purchase_details.receipt_summary.value;
}

/**
 * Creates an <img> element that will render a product thumbnail.
 *
 * @param {Object} imageDetailsObj
 * @return {jQuery}
 */
function makeProductThumbnail(imageDetailsObj) {
    return $('<img />', {
        alt: imageDetailsObj.alt,
        class: 'receipt-img',
        height: '60px',
        loading: imageDetailsObj.loading,
        sizes: imageDetailsObj.sizes,
        src: imageDetailsObj.src,
        srcset: imageDetailsObj.srcset,
        width: '60px',
    });
}

/**
 * Creates a line item wrapper element.
 *
 * @param {String=} classes Optional space seperated list of classes to add.
 * @return {jQuery}
 */
function makeLineItemShell(classes) {
    return $('<div></div>', { class: 'receipt-line-item' }).addClass(classes);
}

/**
 * Returns tooltip wrapped in a styled <span> element.
 *
 * @param {String} tooltip
 * @return {jQuery}
 */
function makeLineItemTooltip(tooltip) {
    return $('<span></span>', {
        'class': 'tt cicon cicon-questionmark-stroke',
        'data-toggle': 'tooltip',
        'title': tooltip,
    });
}

/**
 * Returns a line item title element.
 *
 * @param {String} title
 * @param {String=} classes
 * @return {jQuery}
 */
function makeLineItemTitle(title, classes) {
    return $('<div></div>', {
        class: 'receipt-line-item-title',
        text: $('<p></p>').html(title).text(), // render HTML entities correctly
    }).addClass(classes);
}

/**
 * Returns a line item display element.
 *
 * @param {string} display
 * @param {string=} className optional css class name to add to element
 * @return {jQuery}
 */
function makeLineItemDetails(display, className = '') {
    return $('<div></div>', {
        class: `receipt-line-item-display ${className}`,
        text: display,
    });
}

/**
 * @returns {Array<jQuery>} A list of line item markup that should be added to dom.
 */
function marshalReceipt() {
    const lineItemMarkup = [];
    getReceiptSummary()
        .filter(({ type }) => type !== LINE_ITEM_TYPES.TOTAL)
        .forEach(({ display, label, type, description, children, help_text: helpText }) => {
            const typeClass = (type || '').toLowerCase().replaceAll('_', '-');
            const hasChildren = children && children.length > 0;
            const $title = makeLineItemTitle(
                label,
                hasChildren || description ? `line-item-group-head js-line-item-group-head js-type-${typeClass}` : '',
            );
            if (helpText) {
                if (
                    type === LINE_ITEM_TYPES.PROCESSING_FEE ||
                    (type === LINE_ITEM_TYPES.SALES_TAX && canShowTaxExemptionModal) ||
                    type === LINE_ITEM_TYPES.DUTIES
                ) {
                    $title.append(makeLineItemTooltip(helpText));
                }
            }

            // TODO (CHAIR-17547) update following makeLineItemDetails argument once test is done
            const checkoutStep = getFarthestEditedStep() || 0;
            let updatedDisplay = display;
            if (
                type === 'shipping_fee' &&
                !checkoutStep &&
                !['offer_counter', 'offer_reactivate'].includes(getUrlName())
            ) {
                updatedDisplay = TBD_VALUE_DISPLAY;
            }

            lineItemMarkup.push(makeLineItemShell(typeClass).append($title, makeLineItemDetails(updatedDisplay)));
            if (description) {
                // Add the collapsable description
                const $descriptionShell = makeLineItemShell(
                    `line-item-group js-line-item-group js-type-${typeClass} hidden`,
                );
                $descriptionShell.append($('<div></div>', { class: 'line-item-description' }).text(description));
                lineItemMarkup.push($descriptionShell);
            }
            if (hasChildren) {
                const $childShell = $('<div></div>', {
                    class: `line-item-group js-line-item-group hidden js-type-${typeClass}`,
                });
                children.forEach((child) =>
                    $childShell.append(
                        makeLineItemShell().append(makeLineItemTitle(child.label), makeLineItemDetails(child.display)),
                    ),
                );
                lineItemMarkup.push($childShell);
            }
        });

    return lineItemMarkup;
}

/**
 * Given a receipt, creates the 'total' line item.
 *
 * @returns {jQuery}
 */

function marshalOfferPrice() {
    const product = getPurchaseObject().products[0];
    const productPrice = parseInt(product.your_price.current.value, 10);
    const offerPrice = parseInt(product.price.value, 10);
    const percentOff = getPercentOff(productPrice, offerPrice);
    const label = isPrivateOffer() ? 'Your private offer' : 'Your offer';
    const titleString = percentOff ? `${label} (-${percentOff}%)` : `${label}`;
    let $title;
    if (hasMaoModal) {
        $title = makeLineItemTitle(titleString).append('<button class="btn-link js-edit-price">edit</button>');
    } else {
        $title = makeLineItemTitle(`${titleString}:`);
    }

    return makeLineItemShell().append($title, makeLineItemDetails(formatCurrency(offerPrice, true, false)));
}

/**
 * @returns {Array.<jQuery>} A list of product markup that should be added to dom.
 */
function marshalProducts() {
    const productEls = [];

    getPurchaseObject().products.forEach((product) => {
        const $product = $('#js-card-products .js-card-content').find(
            `.js-product[data-product-id=${product.id?.value}]`,
        );

        let title = decodeHTMLEntity($product.find('.js-product-title').text() || getOfferProductTitle());

        const quantity = product.quantity?.value || 1;
        if (quantity > 1) {
            title += ` (Qty: ${quantity})`;
        }

        const $title = makeLineItemTitle(title);
        if (hasMaoModal && isOffer() && product.image_details_dict) {
            $title.prepend(makeProductThumbnail(product.image_details_dict));
        }

        productEls.push(
            makeLineItemShell().append(
                $title,
                makeLineItemDetails(
                    formatCurrency(product.your_price.current.value, true, false),
                    isOffer() ? 'prior-price' : '',
                ),
            ),
        );
    });

    return productEls;
}

/**
 * @returns {jQuery} The markup for the 'Total' line item
 */
function marshalTotal() {
    const receipt = getReceiptSummary();
    const total = receipt.find(({ type }) => type === LINE_ITEM_TYPES.TOTAL);
    const salesTax = receipt.find(({ type }) => type === LINE_ITEM_TYPES.SALES_TAX);

    const $totalShell = $('<div></div>', { class: 'js-total total' });

    if (salesTax.help_text && canShowTaxExemptionModal) {
        $totalShell.append(
            $('<button></button>', {
                class: 'btn-link add-certificate js-add-certificate',
                type: 'button',
                text: getTaxExemptionButtonText(),
            }),
        );
    }

    // TODO (CHAIR-17547) remove below total display logic when test is done
    let totalDisplay = total.display;
    let totalValue = total.value;
    const checkoutStep = getFarthestEditedStep() || 0;
    const urlName = getUrlName();

    if (!checkoutStep && !['offer_counter', 'offer_reactivate'].includes(urlName)) {
        totalValue -= receipt.find(({ type }) => type === 'shipping_fee').value;
        totalDisplay = `$${totalValue.toFixed(2)}`;
    }

    $totalShell.append(makeLineItemShell().append(makeLineItemTitle(total.label), makeLineItemDetails(totalDisplay)));
    return $totalShell;
}

class ReceiptView {
    constructor($el, callbacks) {
        this.$el = $el;
        this.$titleEl = $('.js-order-summary-title');
        this.$topOfferPrice = isOffer() ? $('.js-top-offer-price') : null;
        this.checkoutCallbacks = callbacks;
    }

    render() {
        this.renderTitle();
        this.renderProducts();
        this.renderOfferPrice();
        this.renderReceipt();
        this.renderIsSignatureRequired();
        this.renderTotal();
    }

    /**
     * @inheritdoc
     */
    bind() {
        //
        // Tax Exemption modal
        //
        this.$el.on('click', '.js-add-certificate', (e) => {
            e.preventDefault();

            const shiptoAddress = getPurchaseObject().shipto_address;
            const addressObj = {};
            Object.keys(shiptoAddress).forEach((key) => {
                if (shiptoAddress[key]) {
                    addressObj[key] = shiptoAddress[key].value;
                }
            });
            showTaxExemptionModal(addressObj.state_name, addressObj);
        });

        //
        // Expand child items in receipt
        //
        this.$el.on('click', '.js-line-item-group-head', (e) => {
            e.preventDefault();
            const lineItemTypeClass = e.currentTarget.className
                .split(' ')
                .find((className) => className.startsWith('js-type'));
            this.$el.find(`.js-line-item-group.${lineItemTypeClass}`).toggleClass('hidden');
        });
    }

    /**
     * @returns {string} The receipt's 'total' line item.
     */
    getTotal() {
        return getReceiptSummary().find(({ type }) => type === LINE_ITEM_TYPES.TOTAL).value;
    }

    /**
     * Renders the 'Your Offer' line item for MAO pages.
     */
    renderOfferPrice() {
        if (isOffer()) {
            const $offerPrice = marshalOfferPrice();
            this.$el.find('.js-offer-price').html($offerPrice);
            if (this.$topOfferPrice?.length > 0) {
                const product = getPurchaseObject().products[0];
                let unitTypeDisplay = product.your_price.current.unit_type?.display;
                unitTypeDisplay = unitTypeDisplay ? `/${unitTypeDisplay}` : '';
                this.$topOfferPrice.text(`${formatCurrency(parseInt(product.price.value, 10))}${unitTypeDisplay}`);
            }
        }
    }

    /**
     * Renders product line items in the running receipt
     */
    renderProducts() {
        this.$el.find('.js-products').html(marshalProducts());
    }

    /**
     * Renders monetary line items in the running receipt
     */
    renderReceipt() {
        this.$el.find('.js-line-items').html(marshalReceipt());
        this.$el.find('.tt').tooltip({ placement: TooltipHelper.getPlacement });
    }

    /**
     * Renders the receipt's title.
     */
    renderTitle() {
        const titleText = getOrderSummaryCardTitle();
        const { products } = getPurchaseObject();
        const orderQuantity = products.reduce((total, { quantity }) => total + (quantity?.value || 1), 0);
        this.$titleEl.text(orderQuantity === 1 ? titleText : `${titleText} (${orderQuantity} items)`);
    }

    renderTotal() {
        this.$el.find('.js-total').replaceWith(marshalTotal());
    }

    renderIsSignatureRequired() {
        const { products } = getPurchaseObject();
        const isSignatureRequired = products.some(
            (product) =>
                product.shipping_partner_code &&
                product.shipping_partner_code.choices.some((choice) => {
                    if (choice.value === product.shipping_partner_code.value && choice.signature_requirement_display) {
                        $('.js-receipt-signature-required').text(choice.signature_requirement_display);
                        return true;
                    }
                    return false;
                }),
        );
        if (!isSignatureRequired) {
            $('.js-receipt-signature-required').text('');
        }
    }
}

export default ReceiptView;
